import { lifecycle, withHandlers, withStateHandlers, withProps, compose } from 'recompose'
import { rest } from 'services'
import { STORE_DETAILS_SCHEMA_ID } from 'constant'
import { STICKY_OFFSET } from './constants'
import TrumpetRatings from './TrumpetRatings'
import { QUALTRICS_SURVEY_URL } from '../../constants'

const withReviewsState = withStateHandlers(
  {
    loading: false,
    wrapperRef: null,
    reviewsData: [],
    currentPage: 1,
    itemReviewed: null,
    nestedReviews: null
  },
  {
    setWrapperRef: () => wrapperRef => ({ wrapperRef }),
    setLoading: () => loading => ({ loading }),
    setReviewsData: () => reviewsData => ({ reviewsData }),
    setCurrentPage: () => currentPage => ({ currentPage }),
    setItemReviewed: () => itemReviewed => ({ itemReviewed }),
    setNestedReviews: () => nestedReviews => ({ nestedReviews })
  }
)

const withGetReviews = withHandlers({
  getReviews: ({ storeNum, setLoading, setReviewsData }) => pageIndex => {
    setLoading(true)

    rest.api.getTrumpetPaginatedStoreReviews({ storeNum, pageIndex })
      .then(({ data }) => setReviewsData(data))
      .then(() => setLoading(false))
      .catch((err) => {
        console.error(err)
        setLoading(false)
      })
  }
})

const withPaginationProps = withProps(({ reviewsData, currentPage, storeNum }) => {
  const hasReviews = reviewsData && !!reviewsData.length
  return {
    showPrev: hasReviews && currentPage > 1,
    showNext: hasReviews && reviewsData.length === 25,
    hasReviews,
    writeReviewURL: QUALTRICS_SURVEY_URL
  }
})

const withHandlePageChange = withHandlers({
  handlePageChange: ({ setCurrentPage, getReviews, wrapperRef }) => newCurrentPage => {
    window.scrollTo(0, wrapperRef.offsetTop - STICKY_OFFSET)
    setCurrentPage(newCurrentPage)
    getReviews(newCurrentPage)
  }
})

const withPaginationHandlers = withHandlers({
  handlePrevClick: ({ currentPage, handlePageChange }) => () => handlePageChange(currentPage - 1),
  handleNextClick: ({ currentPage, handlePageChange }) => () => handlePageChange(currentPage + 1)
})

const withGenerateSchemaProps = withHandlers({
  generateSchemaProps: ({ setItemReviewed, setNestedReviews, reviewsData }) => () => {
    setTimeout(() => {
      const storeDetailsSchemaScript = document.querySelector(`#${STORE_DETAILS_SCHEMA_ID}`)
      const storeDetailsSchemaObj = storeDetailsSchemaScript && JSON.parse(storeDetailsSchemaScript.innerHTML)
      const { image, name, telephone, address } = storeDetailsSchemaObj || {}

      setItemReviewed({
        '@type': 'TireShop',
        image,
        name,
        priceRange: '$$$',
        telephone,
        address
      })
    })

    const reviews = [...reviewsData]
      .map(({ Rating, SurveyDate, ReviewerName, ReviewMessage }) =>
        ({
          '@context': 'https://schema.org',
          '@type': 'Review',
          reviewRating: {
            '@type': 'Rating',
            bestRating: '5',
            ratingValue: `${Rating}`,
            worstRating: '1'
          },
          author: ReviewerName,
          datePublished: SurveyDate,
          reviewBody: ReviewMessage
        }))

    setNestedReviews(reviews)
  }
})

const withLifecycle = lifecycle({
  componentDidMount() {
    const { currentPage, getReviews } = this.props
    getReviews(currentPage)
  },
  componentDidUpdate({ loading: prevLoading }) {
    const { reviewsData, loading, generateSchemaProps } = this.props
    !loading && prevLoading !== loading && reviewsData && reviewsData.length && generateSchemaProps()
  }
})

export default compose(
  withReviewsState,
  withGetReviews,
  withPaginationProps,
  withHandlePageChange,
  withPaginationHandlers,
  withGenerateSchemaProps,
  withLifecycle
)(TrumpetRatings)
